import { useState, ChangeEvent } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { FormControlLabel, IconButton, Switch } from "@mui/material";
import { ArtistsBrief } from "../../../api/artists-briefs";
import useArtistsBriefsQuery from "../hooks/useArtistsBriefsQuery";
import useSaveArtistsBriefsMutation, {
  getSaveBriefsErrorMessage,
} from "../hooks/useSaveArtistsBriefsMutation";
import useBriefImageUpload from "../hooks/useBriefImageUpload";
import DSBox from "../../system-design/DSBox";
import DSGrid from "../../system-design/DSGrid";
import DSCard from "../../system-design/DSCard";
import DSTypography from "../../system-design/DSTypography";
import DSTextField from "../../system-design/DSTextField";
import DSUploadButton from "../../system-design/DSUploadButton";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSAlert from "../../system-design/DSAlert";

const sectionSx = {
  width: "80%",
  mx: "auto",
  backgroundColor: "paper.elevation4",
  p: 2,
  borderRadius: "16px",
  mb: 2,
} as const;

const fieldRowSx = {
  display: "flex",
  alignItems: "center",
  mb: 2,
} as const;

const ArtistsBriefModal = (props: {
  row?: ArtistsBrief;
  setShowModal: (show: boolean) => void;
}) => {
  const { data: briefs } = useArtistsBriefsQuery();
  const {
    mutate: saveBriefs,
    isPending,
    isError: saveError,
    error: saveErrorObj,
  } = useSaveArtistsBriefsMutation({
    onSuccess: () => props.setShowModal(false),
  });

  const [title, setTitle] = useState<string>(props.row?.title || "");
  const [description, setDescription] = useState<string>(
    props.row?.description || "",
  );
  const [labels, setLabels] = useState<string>(
    (props.row?.labels || []).join(", "),
  );
  const [active, setActive] = useState<boolean>(props.row?.active ?? false);
  const { imageUrl, isUploading, uploadError, handleFileChange } =
    useBriefImageUpload(props.row?.imageUrl || "");

  const handleSave = () => {
    if (!props.row || !briefs) return;
    const edited: ArtistsBrief = {
      ...props.row,
      title,
      description,
      imageUrl,
      labels: labels
        .split(",")
        .map((label) => label.trim())
        .filter(Boolean),
      active,
    };
    saveBriefs(
      briefs.map((brief) => (brief.id === props.row!.id ? edited : brief)),
    );
  };

  return (
    <>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginLeft: "10px",
          marginRight: "5px",
          marginBottom: "24px",
        }}
      >
        <DSTypography
          variant="h4"
          sx={{ margin: 0, paddingTop: "16px", paddingLeft: "14px" }}
        >
          Edit Card
        </DSTypography>
        <IconButton
          onClick={() => props.setShowModal(false)}
          sx={{ bottom: "7px", position: "relative" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DSBox>

      <DSBox sx={{ padding: 2 }}>
        <DSGrid container spacing={2}>
          <DSGrid item xs={12}>
            <form id="artistsBriefForm">
              <DSGrid container spacing={2}>
                <DSGrid item sx={sectionSx}>
                  <DSBox sx={fieldRowSx}>
                    <DSTypography sx={{ padding: 1, fontWeight: "bold" }}>
                      Title:
                    </DSTypography>
                    <DSBox sx={{ flex: 1 }}>
                      <DSTextField
                        value={title}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setTitle(e.target.value)
                        }
                        placeholder="Enter card title..."
                        required
                        fullWidth
                        size="small"
                        inputProps={{ "aria-label": "Title", maxLength: 80 }}
                        helperText={`${title.length}/80`}
                      />
                    </DSBox>
                  </DSBox>

                  <DSBox sx={fieldRowSx}>
                    <DSTypography sx={{ padding: 1, fontWeight: "bold" }}>
                      Description:
                    </DSTypography>
                    <DSBox sx={{ flex: 1 }}>
                      <DSTextField
                        value={description}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setDescription(e.target.value)
                        }
                        placeholder="Enter description..."
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        inputProps={{
                          "aria-label": "Description",
                          maxLength: 500,
                        }}
                        helperText={`${description.length}/500`}
                      />
                    </DSBox>
                  </DSBox>

                  <DSBox sx={fieldRowSx}>
                    <DSTypography sx={{ padding: 1, fontWeight: "bold" }}>
                      Labels:
                    </DSTypography>
                    <DSBox sx={{ flex: 1 }}>
                      <DSTextField
                        value={labels}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setLabels(e.target.value)
                        }
                        placeholder="Flat 2D, Motion, 3D"
                        fullWidth
                        size="small"
                        inputProps={{ "aria-label": "Labels" }}
                      />
                    </DSBox>
                  </DSBox>

                  <DSBox sx={{ display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={active}
                          onChange={(e) => setActive(e.target.checked)}
                        />
                      }
                      label="Active (visible on the site)"
                    />
                  </DSBox>
                </DSGrid>

                <DSGrid item sx={sectionSx}>
                  <DSCard
                    sx={{ mb: 2, p: 2, textAlign: "center", width: "100%" }}
                  >
                    {uploadError && (
                      <DSAlert severity="error" sx={{ mb: 2 }}>
                        {uploadError}
                      </DSAlert>
                    )}
                    {isUploading ? (
                      <DSBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: 120,
                        }}
                      >
                        <DSCircularProgress />
                      </DSBox>
                    ) : (
                      <>
                        <DSBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1,
                          }}
                        >
                          <label style={{ width: "100%" }}>
                            <DSUploadButton
                              text={imageUrl ? "Change Image" : "Upload Image"}
                            />
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleFileChange}
                            />
                          </label>
                        </DSBox>
                        {imageUrl && (
                          <DSBox>
                            <DSTypography
                              color="text.secondary"
                              variant="caption"
                            >
                              Current Image Preview:
                            </DSTypography>
                            <br />
                            <img
                              src={imageUrl}
                              alt={title}
                              style={{
                                maxWidth: "120px",
                                maxHeight: "120px",
                                borderRadius: "8px",
                                marginTop: "5px",
                                objectFit: "cover",
                              }}
                            />
                          </DSBox>
                        )}
                      </>
                    )}
                  </DSCard>
                </DSGrid>

                <DSGrid item xs={12}>
                  {saveError && (
                    <DSAlert severity="error" sx={{ mb: 2 }}>
                      {getSaveBriefsErrorMessage(saveErrorObj)}
                    </DSAlert>
                  )}
                  <DSBox
                    sx={{
                      display: "flex",
                      justifyContent: "right",
                      marginTop: "5px",
                    }}
                  >
                    <DSPrimaryButton
                      disabled={isPending || isUploading || !title}
                      onClick={handleSave}
                    >
                      Save
                    </DSPrimaryButton>
                  </DSBox>
                </DSGrid>
              </DSGrid>
            </form>
          </DSGrid>
        </DSGrid>
      </DSBox>
    </>
  );
};

export default ArtistsBriefModal;
